import { useState, useEffect, useRef } from 'react';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2, Clock, Copy, Eye, Star, StarOff, ChevronDown, ChevronUp, StickyNote, Sparkles, ExternalLink, ImageIcon, Upload, Scan, Brain, CheckCircle, PenLine, Plus, Trash2, GripVertical } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClientSelect } from '../lib/ClientSelect';
import { toast } from 'sonner';
import api from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;
const NONE = '__none__';
const opt = (items) => [{ v: NONE, l: 'Any' }, ...items.map(i => ({ v: i, l: i }))];
const PRODUCT_TYPES = opt(['Ring', 'Earrings', 'Pendant', 'Necklace', 'Bracelet', 'Bangle', 'Flexi Bangle', 'Kada', 'Bridal Set']);
const CATEGORIES = opt(['Bridal', 'Daily Wear', 'Lightweight', 'High Jewellery', 'Export', "Men's Jewellery", 'Kids Jewellery']);
const WEIGHT_RANGES = opt(['Under 5g', '5-10g', '10-20g', '20-40g', '40g+']);
const STONE_TYPES = opt(['Diamond', 'Polki', 'CZ', 'Emerald', 'Ruby', 'Sapphire', 'No Stone', 'Mixed']);
const STYLE_DIRECTIONS = ['Minimal', 'Modern', 'Vintage', 'Floral', 'Geometric', 'Bold', 'Flexible / Adjustable', 'Stackable'];
const MFG_CONSTRAINTS = opt(['Easy production', 'Moderate', 'Complex allowed']);
const AI_MODELS = [{ id: 'chatgpt', label: 'ChatGPT' }, { id: 'claude', label: 'Claude' }];
const toVal = (v) => v === NONE ? '' : v;
const fromVal = (v) => v || NONE;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY_FORM = { product_type: '', category: '', weight_range: '', stone_type: '', style_direction: [], keywords: '', reference_client: '', manufacturing_constraint: '', search_type: 'product' };

// Editable spec dropdown options for AI Decoder
const PRODUCT_TYPES_EDIT = ['Ring', 'Earrings', 'Pendant', 'Necklace', 'Bracelet', 'Bangle', 'Flexi Bangle', 'Kada', 'Bridal Set', 'Brooch', 'Anklet', 'Nose Pin', 'Maang Tikka'];
const STONE_TYPES_EDIT = ['Diamond', 'Ruby', 'Emerald', 'Sapphire', 'CZ', 'Polki', 'Moissanite', 'Pearl', 'Topaz', 'Amethyst', 'Garnet', 'Tourmaline', 'Opal', 'Onyx', 'Zircon'];
const STONE_CATS_EDIT = ['Center', 'Accent', 'Side', 'Halo', 'Pave', 'Surround'];
const STONE_SHAPES_EDIT = ['Round', 'Princess', 'Oval', 'Pear', 'Marquise', 'Cushion', 'Emerald', 'Baguette', 'Radiant', 'Asscher', 'Heart', 'Trillion', 'Rose Cut', 'Briolette'];
const SETTING_TYPES_EDIT = ['Prong', 'Bezel', 'Channel', 'Pave', 'Micro-pave', 'Invisible', 'Tension', 'Flush', 'Bar', 'Cluster', 'Gypsy', 'Burnish'];
const METAL_TYPES_EDIT = ['Gold', 'Platinum', 'Silver', 'White Gold', 'Rose Gold', 'Palladium'];
const METAL_KARATS_EDIT = ['9K', '10K', '14K', '18K', '22K', '24K', '950 Pt', 'S925'];
const METAL_COLORS_EDIT = ['Yellow', 'White', 'Rose', 'Two-tone', 'Tri-tone', 'Green', 'Black'];
const CLASP_TYPES_EDIT = ['Lobster', 'Spring Ring', 'Toggle', 'Box', 'Hook', 'Push-pull', 'Barrel', 'Magnetic', 'Fold-over', 'S-hook'];
const GALLERY_TYPES_EDIT = ['Open', 'Closed', 'Cathedral', 'Basket', 'Trellis', 'Filigree'];
const SHANK_TYPES_EDIT = ['Comfort-fit', 'Knife-edge', 'Split', 'Plain', 'Tapered', 'Twisted', 'Bypass', 'Euro-shank'];
const BACK_TYPES_EDIT = ['Push-back', 'Screw-back', 'Lever-back', 'Omega', 'Clip-on', 'Hook', 'Hinged'];
const LINK_TYPES_EDIT = ['Cable', 'Curb', 'Box', 'Rope', 'Figaro', 'Singapore', 'Wheat', 'Anchor', 'Venetian', 'Snake'];
const COMPLEXITY_OPTS = ['Low', 'Medium', 'High'];
const COLLECTION_OPTS = ['Low', 'Medium', 'High'];
const FINISH_OPTS = ['High Polish', 'Matte', 'Satin', 'Brushed', 'Hammered', 'Rhodium', 'Antique', 'Sand Blast', 'Mirror'];

export default function ProductDevAIPage() {
  const [mainTab, setMainTab] = useState('search'); // 'search' or 'decoder'
  // Search state
  const [searchMode, setSearchMode] = useState('text'); // 'text' or 'image'
  const [subPanel, setSubPanel] = useState('form'); // 'form', 'gallery', 'picks', 'history'
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [searching, setSearching] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [pastRequests, setPastRequests] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [decoderInitUrl, setDecoderInitUrl] = useState(null); // for gallery→decoder flow

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try { const r = await api.get(`${API}/api/pd-ai/requests`, getAuth()); setPastRequests(r.data); } catch {}
    finally { setLoadingHistory(false); }
  };

  const toggleStyle = (s) => setForm(p => ({ ...p, style_direction: p.style_direction.includes(s) ? p.style_direction.filter(x => x !== s) : [...p.style_direction, s] }));

  const handleSearch = async () => {
    setSearching(true);
    try {
      const res = await api.post(`${API}/api/pd-ai/search`, form, { ...getAuth(), timeout: 30000 });
      setCurrentRequest(res.data); setSubPanel('gallery');
      toast.success(`Found ${res.data.images?.length || 0} designs`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Search failed'); }
    finally { setSearching(false); }
  };

  const handleImageSearch = async (base64, mimeType, filters = {}) => {
    setSearching(true);
    try {
      const res = await api.post(`${API}/api/pd-ai/image-search`, { image_base64: base64, mime_type: mimeType, ...filters }, { ...getAuth(), timeout: 120000 });
      setCurrentRequest(res.data); setSubPanel('gallery');
      toast.success(`Found ${res.data.images?.length || 0} similar designs`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Image search failed'); }
    finally { setSearching(false); }
  };

  const handleAnalyze = async (model) => {
    if (!currentRequest) return;
    setAnalyzing(true);
    try {
      const res = await api.post(`${API}/api/pd-ai/analyze`, { request_id: currentRequest.id, ai_model: model }, { ...getAuth(), timeout: 120000 });
      setCurrentRequest(prev => ({ ...prev, results: res.data.results, ai_analyzed: true, ai_model: model }));
      setSubPanel('picks');
      toast.success(`AI selected top ${res.data.results?.length || 0} designs`);
    } catch (err) { toast.error(err.response?.data?.detail || 'AI analysis failed'); }
    finally { setAnalyzing(false); }
  };

  const handleViewRequest = async (id) => {
    try {
      const res = await api.get(`${API}/api/pd-ai/requests/${id}`, getAuth());
      setCurrentRequest(res.data); setSubPanel(res.data.ai_analyzed ? 'picks' : 'gallery');
    } catch { toast.error('Failed to load'); }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await api.post(`${API}/api/pd-ai/requests/${id}/duplicate`, {}, getAuth());
      setForm({ ...EMPTY_FORM, ...res.data }); setSubPanel('form'); setSearchMode('text');
      toast.success('Parameters copied');
    } catch { toast.error('Failed'); }
  };

  const handleToggleSave = async (resultId, saved) => {
    if (!currentRequest) return;
    try {
      await api.put(`${API}/api/pd-ai/requests/${currentRequest.id}/results/${resultId}`, { saved: !saved }, getAuth());
      setCurrentRequest(prev => ({ ...prev, results: prev.results.map(r => r.id === resultId ? { ...r, saved: !saved } : r) }));
    } catch { toast.error('Failed'); }
  };

  const handleUpdateNotes = async (resultId, notes) => {
    if (!currentRequest) return;
    try {
      await api.put(`${API}/api/pd-ai/requests/${currentRequest.id}/results/${resultId}`, { notes }, getAuth());
      setCurrentRequest(prev => ({ ...prev, results: prev.results.map(r => r.id === resultId ? { ...r, notes } : r) }));
      toast.success('Notes saved');
    } catch { toast.error('Failed'); }
  };

  const handleDecodeFromUrl = (imageUrl) => {
    setDecoderInitUrl(imageUrl);
    setMainTab('decoder');
  };

  return (
    <CollapsibleLayout>
      <div className="flex h-[calc(100vh-48px)] bg-background" data-testid="pd-ai-page">
        {/* LEFT PANEL — 2 main tabs */}
        <div className="w-48 border-r border-border bg-card flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-border">
            <h1 className="text-sm font-bold text-foreground flex items-center gap-2"><Sparkles size={15} className="text-amber-500" />PD AI</h1>
          </div>
          {[
            { id: 'search', label: 'New Search', icon: Search },
            { id: 'decoder', label: 'AI Decoder', icon: Scan },
          ].map(tab => (
            <button key={tab.id} onClick={() => { setMainTab(tab.id); if (tab.id === 'search') setSubPanel('form'); }}
              data-testid={`pd-main-tab-${tab.id}`}
              className={`flex items-center gap-2.5 px-4 py-3 text-xs font-medium text-left transition-colors border-l-2 ${mainTab === tab.id ? 'bg-primary/5 border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
            >
              <tab.icon size={15} />{tab.label}
            </button>
          ))}

          {/* Learning stats at bottom */}
          <div className="mt-auto p-3 border-t border-border">
            <LearningIndicator />
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {mainTab === 'search' && (
            <>
              {/* Sub-panel top bar */}
              <div className="flex items-center justify-between px-4 py-1.5 border-b border-border bg-card/50 flex-shrink-0">
                <div className="flex gap-1">
                  {[
                    { id: 'form', label: 'Search' },
                    { id: 'gallery', label: 'Gallery', disabled: !currentRequest },
                    { id: 'picks', label: 'AI Picks', disabled: !currentRequest?.ai_analyzed },
                    { id: 'history', label: 'History', onClick: fetchHistory },
                  ].map(sp => (
                    <button key={sp.id} disabled={sp.disabled}
                      onClick={() => { setSubPanel(sp.id); sp.onClick?.(); }}
                      data-testid={`pd-sub-${sp.id}`}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${subPanel === sp.id ? 'bg-primary text-primary-foreground' : sp.disabled ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                    >{sp.label}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-auto">
                {subPanel === 'form' && <SearchPanel form={form} setForm={setForm} toggleStyle={toggleStyle} searching={searching} searchMode={searchMode} setSearchMode={setSearchMode} onSearch={handleSearch} onImageSearch={handleImageSearch} />}
                {subPanel === 'gallery' && currentRequest && <GalleryPanel request={currentRequest} analyzing={analyzing} onAnalyze={handleAnalyze} onBack={() => setSubPanel('form')} onDecodeImage={handleDecodeFromUrl} />}
                {subPanel === 'picks' && currentRequest?.ai_analyzed && <PicksPanel request={currentRequest} onToggleSave={handleToggleSave} onUpdateNotes={handleUpdateNotes} onBack={() => setSubPanel('gallery')} onRerun={() => { setForm({ ...EMPTY_FORM, ...currentRequest }); setSubPanel('form'); }} />}
                {subPanel === 'history' && <HistoryPanel requests={pastRequests} loading={loadingHistory} onView={handleViewRequest} onDuplicate={handleDuplicate} />}
              </div>
            </>
          )}
          {mainTab === 'decoder' && <DecoderPanel initImageUrl={decoderInitUrl} onInitConsumed={() => setDecoderInitUrl(null)} />}
        </div>
      </div>
    </CollapsibleLayout>
  );
}

/* ============ LEARNING INDICATOR ============ */
function LearningIndicator() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get(`${API}/api/pd-ai/learning/stats`, getAuth()).then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return null;
  return (
    <div className="text-[10px] text-muted-foreground space-y-0.5" data-testid="pd-learning-stats">
      <div className="flex items-center gap-1"><Brain size={10} className={stats.learning_active ? 'text-green-500' : 'text-muted-foreground/40'} /><span className="font-medium">{stats.learning_active ? 'Learning Active' : 'Learning'}</span></div>
      <div>{stats.total_searches} searches</div>
      <div>{stats.total_corrections} corrections</div>
      <div>{stats.total_decodes} decodes</div>
    </div>
  );
}

/* ============ SEARCH PANEL (Text + Image) ============ */
function SearchPanel({ form, setForm, toggleStyle, searching, searchMode, setSearchMode, onSearch, onImageSearch }) {
  const [imgPreview, setImgPreview] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgFilters, setImgFilters] = useState({ product_type: '', category: '', weight_range: '', stone_type: '' });
  const imgRef = useRef(null);

  const handleImgFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImgFile(f);
    const reader = new FileReader();
    reader.onload = () => setImgPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleImgSearch = async () => {
    if (!imgFile) return;
    const reader = new FileReader();
    const base64 = await new Promise(resolve => { reader.onload = () => resolve(reader.result.split(',')[1]); reader.readAsDataURL(imgFile); });
    onImageSearch(base64, imgFile.type || 'image/jpeg', imgFilters);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="pd-search-panel">
      <div>
        <h2 className="text-base font-bold text-foreground">Search Designs</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Search by parameters or upload a reference image.</p>
      </div>

      {/* Search mode toggle */}
      <div className="flex gap-2">
        <button onClick={() => setSearchMode('text')} data-testid="pd-mode-text"
          className={`flex-1 p-2.5 rounded-lg border text-left transition-colors ${searchMode === 'text' ? 'bg-primary/10 border-primary' : 'bg-card border-border hover:border-primary/40'}`}>
          <div className="text-xs font-semibold text-foreground flex items-center gap-1.5"><Search size={13} />Text Search</div>
          <div className="text-[10px] text-muted-foreground">By keywords, filters & style</div>
        </button>
        <button onClick={() => setSearchMode('image')} data-testid="pd-mode-image"
          className={`flex-1 p-2.5 rounded-lg border text-left transition-colors ${searchMode === 'image' ? 'bg-primary/10 border-primary' : 'bg-card border-border hover:border-primary/40'}`}>
          <div className="text-xs font-semibold text-foreground flex items-center gap-1.5"><Upload size={13} />Image Search</div>
          <div className="text-[10px] text-muted-foreground">Upload a photo to find similar</div>
        </button>
      </div>

      {searchMode === 'text' ? (
        <>
          {/* Search Type */}
          <div className="flex gap-2">
            {[{ id: 'product', l: 'Products' }, { id: 'cad', l: 'CAD/3D' }, { id: 'sketch', l: 'Sketches' }].map(t => (
              <button key={t.id} onClick={() => setForm(p => ({ ...p, search_type: t.id }))} data-testid={`pd-search-type-${t.id}`}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${form.search_type === t.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}
              >{t.l}</button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <DF label="Product Type" value={form.product_type} options={PRODUCT_TYPES} onChange={v => setForm(p => ({ ...p, product_type: toVal(v) }))} tid="pd-product-type" />
            <DF label="Category" value={form.category} options={CATEGORIES} onChange={v => setForm(p => ({ ...p, category: toVal(v) }))} tid="pd-category" />
            <DF label="Weight" value={form.weight_range} options={WEIGHT_RANGES} onChange={v => setForm(p => ({ ...p, weight_range: toVal(v) }))} tid="pd-weight-range" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DF label="Stone" value={form.stone_type} options={STONE_TYPES} onChange={v => setForm(p => ({ ...p, stone_type: toVal(v) }))} tid="pd-stone-type" />
            <DF label="Manufacturing" value={form.manufacturing_constraint} options={MFG_CONSTRAINTS} onChange={v => setForm(p => ({ ...p, manufacturing_constraint: toVal(v) }))} tid="pd-mfg" />
          </div>
          <div>
            <Label className="text-xs font-medium mb-1.5 block">Style</Label>
            <div className="flex flex-wrap gap-1.5">
              {STYLE_DIRECTIONS.map(s => (
                <button key={s} onClick={() => toggleStyle(s)} className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${form.style_direction.includes(s) ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}>{s}</button>
              ))}
            </div>
          </div>
          <Input value={form.keywords} onChange={e => setForm(p => ({ ...p, keywords: e.target.value }))} placeholder='Keywords: "flexi bangle", "cartier style"' className="h-8 text-xs" data-testid="pd-keywords" />
          <ClientSelect value={form.reference_client} onChange={v => setForm(p => ({ ...p, reference_client: v }))} placeholder="Reference Client..." className="w-full h-8" />

          <Button onClick={onSearch} disabled={searching} className="w-full h-9 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold" data-testid="pd-search-btn">
            {searching ? <><Loader2 size={14} className="mr-2 animate-spin" />Searching...</> : <><Search size={14} className="mr-2" />Search Designs</>}
          </Button>
        </>
      ) : (
        <>
          <div onClick={() => imgRef.current?.click()} data-testid="pd-imgsearch-upload"
            className="border-2 border-dashed border-border rounded-lg h-44 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/30 overflow-hidden">
            {imgPreview ? <img src={imgPreview} alt="Reference" className="max-h-full object-contain" /> : (
              <><Upload size={28} className="text-muted-foreground/40 mb-2" /><p className="text-xs text-muted-foreground">Click to upload reference image</p></>
            )}
          </div>
          <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImgFile} className="hidden" />

          {/* Image search filters */}
          <div className="border border-border rounded-lg p-3 bg-card/50 space-y-2.5">
            <p className="text-[11px] font-semibold text-muted-foreground">Refine Results (optional)</p>
            <Input value={imgFilters.custom_search || ''} onChange={e => setImgFilters(p => ({ ...p, custom_search: e.target.value }))} placeholder="Custom search text, e.g. &quot;art deco diamond ring with milgrain&quot;" className="h-8 text-xs" data-testid="pd-img-custom-search" />
            <div className="grid grid-cols-2 gap-2.5">
              <DF label="Product Type" value={imgFilters.product_type} options={PRODUCT_TYPES} onChange={v => setImgFilters(p => ({ ...p, product_type: toVal(v) }))} tid="pd-img-product-type" />
              <DF label="Category" value={imgFilters.category} options={CATEGORIES} onChange={v => setImgFilters(p => ({ ...p, category: toVal(v) }))} tid="pd-img-category" />
              <DF label="Weight Range" value={imgFilters.weight_range} options={WEIGHT_RANGES} onChange={v => setImgFilters(p => ({ ...p, weight_range: toVal(v) }))} tid="pd-img-weight" />
              <DF label="Stone Type" value={imgFilters.stone_type} options={STONE_TYPES} onChange={v => setImgFilters(p => ({ ...p, stone_type: toVal(v) }))} tid="pd-img-stone" />
            </div>
          </div>

          <Button onClick={handleImgSearch} disabled={!imgFile || searching} className="w-full h-9 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold" data-testid="pd-imgsearch-btn">
            {searching ? <><Loader2 size={14} className="mr-2 animate-spin" />AI analyzing & searching...</> : <><Search size={14} className="mr-2" />Find Similar Designs</>}
          </Button>
        </>
      )}
    </div>
  );
}

function DF({ label, value, options, onChange, tid }) {
  return (
    <div>
      <Label className="text-[11px] font-medium">{label}</Label>
      <Select value={fromVal(value)} onValueChange={onChange}>
        <SelectTrigger className="mt-0.5 h-7 text-xs" data-testid={tid}><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/* ============ GALLERY PANEL ============ */
function GalleryPanel({ request, analyzing, onAnalyze, onBack, onDecodeImage }) {
  const [aiModel, setAiModel] = useState('chatgpt');
  const images = request.images || [];
  const interp = request.interpretation || {};

  return (
    <div className="p-4" data-testid="pd-gallery">
      <div className="flex items-center justify-between mb-3 max-w-7xl mx-auto">
        <div>
          <h2 className="text-sm font-bold text-foreground">{images.length} Images Found</h2>
          {interp.locked_category && <p className="text-[10px] text-muted-foreground">Category: {interp.locked_category}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={onBack}>Edit Search</Button>
          <div className="flex border border-border rounded overflow-hidden">
            {AI_MODELS.map(m => (
              <button key={m.id} onClick={() => setAiModel(m.id)}
                className={`px-2 py-1 text-[10px] font-medium ${aiModel === m.id ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground'}`}
              >{m.label}</button>
            ))}
          </div>
          <Button size="sm" className="h-6 text-[11px] bg-violet-600 hover:bg-violet-700" onClick={() => onAnalyze(aiModel)} disabled={analyzing} data-testid="pd-analyze-btn">
            {analyzing ? <><Loader2 size={11} className="mr-1 animate-spin" />Analyzing...</> : <><Sparkles size={11} className="mr-1" />AI Filter Top 10</>}
          </Button>
        </div>
      </div>

      {interp.expanded_terms?.length > 0 && (
        <div className="max-w-7xl mx-auto mb-3 p-2 bg-blue-50/60 border border-blue-200/50 rounded-lg flex items-center gap-2" data-testid="pd-interpretation-bar">
          <Search size={11} className="text-blue-500 flex-shrink-0" />
          <div className="flex flex-wrap gap-1 text-[10px]">
            {interp.expanded_terms.map((t, i) => <span key={i} className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{t}</span>)}
          </div>
        </div>
      )}

      {analyzing && <div className="max-w-7xl mx-auto mb-3 p-2.5 bg-violet-50 border border-violet-200 rounded-lg text-center text-xs text-violet-700">AI is reviewing {images.length} images... 30-60 seconds.</div>}

      <div className="max-w-7xl mx-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5">
        {images.map((img, idx) => <ImgCard key={idx} img={img} idx={idx} onDecode={onDecodeImage} />)}
      </div>
      {!images.length && <div className="text-center py-20 text-sm text-muted-foreground"><ImageIcon size={28} className="mx-auto mb-2 opacity-30" />No images found.</div>}
    </div>
  );
}

function ImgCard({ img, idx, onDecode }) {
  const [err, setErr] = useState(false);
  return (
    <div className="group relative border border-border rounded-lg overflow-hidden bg-card hover:border-primary/40 transition-colors" data-testid={`pd-gallery-img-${idx}`}>
      <div className="aspect-square bg-muted">{!err ? <img src={img.thumbnail || img.url} alt={img.title} className="w-full h-full object-cover" loading="lazy" onError={() => setErr(true)} /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={18} className="opacity-20" /></div>}</div>
      <div className="p-1.5"><p className="text-[10px] text-foreground line-clamp-2 leading-tight">{img.title}</p>
        {img.source && <a href={img.source} target="_blank" rel="noopener noreferrer" className="text-[9px] text-muted-foreground hover:text-primary flex items-center gap-0.5 mt-0.5"><ExternalLink size={7} />{(() => { try { return new URL(img.source).hostname.replace('www.','').slice(0,18); } catch { return ''; } })()}</a>}
      </div>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
        <a href={img.url} target="_blank" rel="noopener noreferrer" className="bg-white/90 text-[10px] px-2 py-1 rounded font-medium hover:bg-white">View</a>
        {onDecode && <button onClick={(e) => { e.stopPropagation(); onDecode(img.url); }} className="bg-amber-500 text-white text-[10px] px-2 py-1 rounded font-medium hover:bg-amber-600 flex items-center gap-0.5" data-testid={`pd-decode-img-${idx}`}><Scan size={9} />Decode</button>}
      </div>
    </div>
  );
}

/* ============ AI PICKS PANEL ============ */
function PicksPanel({ request, onToggleSave, onUpdateNotes, onBack, onRerun }) {
  const [expandedId, setExpandedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  return (
    <div className="max-w-5xl mx-auto p-4" data-testid="pd-picks">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-foreground">Top {request.results?.length || 0} AI Picks <span className="text-[10px] font-normal bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded ml-1">{request.ai_model === 'claude' ? 'Claude' : 'ChatGPT'}</span></h2>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={onBack}>Gallery</Button>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={onRerun}><Copy size={10} className="mr-1" />New Search</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {request.results?.map((r, i) => (
          <PickCard key={r.id} result={r} rank={i+1} expanded={expandedId===r.id} onExpand={() => setExpandedId(expandedId===r.id?null:r.id)}
            onSave={() => onToggleSave(r.id, r.saved)}
            editingNotes={editingNotes===r.id} notesText={notesText} setNotesText={setNotesText}
            onStartNotes={() => { setEditingNotes(r.id); setNotesText(r.notes||''); }}
            onSaveNotes={() => { onUpdateNotes(r.id, notesText); setEditingNotes(null); }}
            onCancelNotes={() => setEditingNotes(null)} />
        ))}
      </div>
    </div>
  );
}

function PickCard({ result, rank, expanded, onExpand, onSave, editingNotes, notesText, setNotesText, onStartNotes, onSaveNotes, onCancelNotes }) {
  const [err, setErr] = useState(false);
  const c = result.manufacturing_complexity || 'Medium';
  const cc = c === 'Low' ? 'text-green-600 bg-green-50' : c === 'High' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';
  return (
    <div className={`border rounded-lg overflow-hidden ${result.saved ? 'border-amber-400 bg-amber-50/30' : 'border-border bg-card'}`} data-testid={`pd-result-${result.id}`}>
      <div className="relative aspect-square bg-muted">
        {result.image_url && !err ? <img src={result.image_url} alt={result.title} className="w-full h-full object-cover" onError={() => setErr(true)} loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Sparkles size={22} className="opacity-15" /></div>}
        <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">#{rank}</div>
        <button onClick={onSave} className={`absolute top-1.5 right-1.5 p-1 rounded-full ${result.saved ? 'bg-amber-500 text-white' : 'bg-black/40 text-white hover:bg-black/60'}`} data-testid={`pd-save-${result.id}`}>{result.saved ? <Star size={13} fill="currentColor" /> : <StarOff size={13} />}</button>
      </div>
      <div className="p-2">
        <h3 className="text-[11px] font-semibold text-foreground line-clamp-2 mb-1">{result.title}</h3>
        <div className="flex items-center gap-1 flex-wrap">
          {result.estimated_weight_range && <span className="text-[9px] text-muted-foreground">{result.estimated_weight_range}</span>}
          <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${cc}`}>{c}</span>
        </div>
        {result.notes && !editingNotes && <div className="mt-1 p-1 bg-yellow-50 rounded text-[9px] text-yellow-800 line-clamp-2">{result.notes}</div>}
        {editingNotes ? (
          <div className="mt-1 space-y-1"><Textarea value={notesText} onChange={e => setNotesText(e.target.value)} rows={2} className="text-xs h-auto" />
            <div className="flex gap-1"><Button size="sm" className="h-5 text-[9px] px-2" onClick={onSaveNotes}>Save</Button><Button size="sm" variant="ghost" className="h-5 text-[9px] px-2" onClick={onCancelNotes}>Cancel</Button></div>
          </div>
        ) : <button onClick={onStartNotes} className="text-[9px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 mt-1"><StickyNote size={9} />{result.notes?'Edit':'Add'} Notes</button>}
        <button onClick={onExpand} className="w-full flex items-center justify-center gap-1 mt-1 pt-1 border-t border-border/50 text-[9px] text-muted-foreground hover:text-foreground">{expanded?<><ChevronUp size={9}/>Less</>:<><ChevronDown size={9}/>Details</>}</button>
        {expanded && (
          <div className="mt-1.5 space-y-1 text-[10px] text-muted-foreground">
            {result.why_selected && <div><span className="font-semibold text-foreground">Why:</span> {result.why_selected}</div>}
            {result.stone_details && <div><span className="font-semibold text-foreground">Stones:</span> {result.stone_details}</div>}
            {result.suggested_modifications && <div><span className="font-semibold text-foreground">Mods:</span> {result.suggested_modifications}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ HISTORY PANEL ============ */
function HistoryPanel({ requests, loading, onView, onDuplicate }) {
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>;
  if (!requests.length) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">No past searches yet.</div>;
  return (
    <div className="max-w-4xl mx-auto p-4 space-y-2" data-testid="pd-history">
      {requests.map(req => (
        <div key={req.id} className="border border-border rounded-lg p-2.5 bg-card hover:border-primary/30 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold">{req.product_type || 'Wide Search'}</span>
                {req.category && <span className="text-[10px] bg-muted px-1 py-0.5 rounded">{req.category}</span>}
                {req.stone_type && req.stone_type !== 'No Stone' && <span className="text-[10px] bg-amber-50 text-amber-700 px-1 py-0.5 rounded">{req.stone_type}</span>}
                <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${req.ai_analyzed ? 'bg-violet-50 text-violet-600' : 'bg-gray-100 text-gray-500'}`}>{req.ai_analyzed ? 'AI Filtered' : 'Raw'}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5"><Clock size={9} className="inline mr-0.5" />{new Date(req.created_at).toLocaleDateString()} | {req.images?.length||0} images</div>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onDuplicate(req.id)}><Copy size={10} className="mr-1" />Dup</Button>
              <Button size="sm" className="h-6 text-[10px]" onClick={() => onView(req.id)}><Eye size={10} className="mr-1" />View</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============ DECODER PANEL ============ */
function DecoderPanel({ initImageUrl, onInitConsumed }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decoding, setDecoding] = useState(false);
  const [result, setResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedSpecs, setEditedSpecs] = useState({});
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  // Handle gallery→decoder flow: auto-decode from URL
  useEffect(() => {
    if (initImageUrl) {
      setPreview(initImageUrl);
      setFile(null);
      setResult(null);
      onInitConsumed?.();
      decodeFromUrl(initImageUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initImageUrl]);

  const handleFile = (e) => { const f = e.target.files?.[0]; if (!f) return; setFile(f); setResult(null); const r = new FileReader(); r.onload = () => setPreview(r.result); r.readAsDataURL(f); };

  const handleDecode = async () => {
    if (!file) return; setDecoding(true);
    try {
      const r = new FileReader();
      const b64 = await new Promise(res => { r.onload = () => res(r.result.split(',')[1]); r.readAsDataURL(file); });
      const res = await api.post(`${API}/api/pd-ai/decode`, { image_base64: b64, mime_type: file.type || 'image/jpeg' }, { ...getAuth(), timeout: 120000 });
      setResult(res.data); setEditedSpecs(res.data.specs || {}); toast.success('Image decoded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Decode failed'); }
    finally { setDecoding(false); }
  };

  const decodeFromUrl = async (url) => {
    setDecoding(true);
    try {
      const res = await api.post(`${API}/api/pd-ai/decode-url`, { image_url: url }, { ...getAuth(), timeout: 120000 });
      setResult(res.data); setEditedSpecs(res.data.specs || {}); toast.success('Image decoded from search');
    } catch (err) { toast.error(err.response?.data?.detail || 'Decode failed'); }
    finally { setDecoding(false); }
  };

  const handleSaveCorrections = async () => {
    if (!result) return; setSaving(true);
    try {
      await api.post(`${API}/api/pd-ai/decode/${result.id}/correct`, { decode_id: result.id, corrections: editedSpecs }, getAuth());
      setResult(prev => ({ ...prev, specs: editedSpecs, corrections_applied: true })); setEditMode(false);
      toast.success('Corrections saved — AI will learn!');
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handleCreateTask = async () => {
    if (!result) return; setCreating(true);
    try {
      const res = await api.post(`${API}/api/pd-ai/decode/${result.id}/create-task`, {}, getAuth());
      toast.success(`Task created: ${res.data.party_cad_no}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const specs = result?.specs || {};

  return (
    <div className="flex-1 overflow-auto p-4" data-testid="pd-decoder">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2"><Scan size={16} className="text-amber-500" />AI Decoder</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Upload a jewelry image — AI extracts every manufacturing spec.</p>
        </div>

        <div className="flex gap-5">
          {/* Upload area */}
          <div className="w-72 flex-shrink-0">
            <div onClick={() => inputRef.current?.click()} data-testid="pd-decoder-upload"
              className="border-2 border-dashed border-border rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/30 overflow-hidden">
              {preview ? <img src={preview} alt="Upload" className="w-full h-full object-contain" /> : (
                <><Upload size={26} className="text-muted-foreground/40 mb-2" /><p className="text-xs text-muted-foreground">Click to upload</p><p className="text-[10px] text-muted-foreground/50">JPG, PNG, WEBP</p></>
              )}
            </div>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
            <Button onClick={handleDecode} disabled={(!file) || decoding} className="w-full mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="pd-decode-btn">
              {decoding ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Analyzing...</> : <><Scan size={13} className="mr-1.5" />Decode Image</>}
            </Button>
            {decoding && <p className="text-[10px] text-muted-foreground text-center mt-1">GPT-4o Vision analyzing...</p>}
          </div>

          {/* Specs */}
          {result && (
            <div className="flex-1 space-y-2.5 overflow-auto max-h-[calc(100vh-180px)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold">{specs.product_type} — {specs.sub_type}</h3>
                  {specs.confidence_score && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{Math.round(specs.confidence_score * 100)}%</span>}
                  {result.corrections_applied && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Brain size={9} />Learned</span>}
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant={editMode ? "default" : "outline"} className="h-6 text-[11px]" onClick={() => { if (editMode) handleSaveCorrections(); else setEditMode(true); }} disabled={saving} data-testid="pd-decoder-edit">
                    {editMode ? (saving ? <Loader2 size={11} className="mr-1 animate-spin" /> : <CheckCircle size={11} className="mr-1" />) : <PenLine size={11} className="mr-1" />}
                    {editMode ? 'Save' : 'Edit & Teach'}
                  </Button>
                  <Button size="sm" className="h-6 text-[11px] bg-violet-600 hover:bg-violet-700" onClick={handleCreateTask} disabled={creating} data-testid="pd-decoder-create-task">
                    {creating ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Plus size={11} className="mr-1" />}Create Task
                  </Button>
                </div>
              </div>
              {/* Commentary */}
              {specs.commentary && (
                <div className="p-2.5 bg-amber-50/60 border border-amber-200/50 rounded-lg" data-testid="pd-decoder-commentary">
                  <p className="text-xs text-amber-900 leading-relaxed italic">{specs.commentary}</p>
                </div>
              )}
              <SpecsGrid specs={editMode ? editedSpecs : specs} editMode={editMode} onChange={editMode ? setEditedSpecs : undefined} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ SPECS GRID (Fully Editable like CAD Sheet) ============ */
function SpecsGrid({ specs, editMode, onChange }) {
  // Helper to update a top-level field
  const setField = (key, val) => onChange({ ...specs, [key]: val });

  // Helper to update nested object field
  const setNested = (parent, key, val) => {
    onChange({ ...specs, [parent]: { ...(specs[parent] || {}), [key]: val } });
  };

  // Stone helpers
  const addStone = () => {
    const stones = [...(specs.stones || []), { type: 'Diamond', category: 'Accent', shape: 'Round', estimated_count: 1, estimated_carat_total: 0, setting_type: 'Prong', estimated_size_mm: '' }];
    onChange({ ...specs, stones });
  };
  const removeStone = (idx) => {
    const stones = (specs.stones || []).filter((_, i) => i !== idx);
    onChange({ ...specs, stones });
  };
  const updateStone = (idx, key, val) => {
    const stones = (specs.stones || []).map((s, i) => i === idx ? { ...s, [key]: val } : s);
    onChange({ ...specs, stones });
  };

  // Manufacturing techniques helper
  const addTechnique = (t) => {
    if (!t) return;
    const curr = specs.manufacturing_techniques || [];
    if (!curr.includes(t)) onChange({ ...specs, manufacturing_techniques: [...curr, t] });
  };
  const removeTechnique = (idx) => {
    onChange({ ...specs, manufacturing_techniques: (specs.manufacturing_techniques || []).filter((_, i) => i !== idx) });
  };

  // Read-only field display
  const ViewField = ({ label, value, suffix }) => {
    const d = value === null || value === undefined ? '—' : String(value);
    return (<div><span className="text-[10px] text-muted-foreground font-medium">{label}</span><p className="text-xs font-semibold text-foreground">{d}{suffix && d !== '—' ? ` ${suffix}` : ''}</p></div>);
  };

  // Select dropdown for edit mode
  const EditSelect = ({ label, value, options, onSelect }) => (
    <div>
      <span className="text-[10px] text-muted-foreground font-medium block">{label}</span>
      <Select value={value || '__none__'} onValueChange={v => onSelect(v === '__none__' ? '' : v)}>
        <SelectTrigger className="h-6 text-xs mt-0.5"><SelectValue /></SelectTrigger>
        <SelectContent>{[<SelectItem key="__none__" value="__none__">—</SelectItem>, ...options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)]}</SelectContent>
      </Select>
    </div>
  );

  // Text input for edit mode
  const EditInput = ({ label, value, onInput, suffix, type = 'text' }) => (
    <div>
      <span className="text-[10px] text-muted-foreground font-medium block">{label}{suffix ? ` (${suffix})` : ''}</span>
      <Input value={value ?? ''} onChange={e => onInput(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} className="h-6 text-xs mt-0.5" type={type} />
    </div>
  );

  return (
    <div className="space-y-2.5" data-testid="pd-specs-grid">
      {/* ---- BASIC INFO ---- */}
      <div className="p-2.5 border border-border rounded-lg bg-card">
        <h4 className="text-[10px] font-bold text-muted-foreground uppercase mb-1.5">Basic</h4>
        {editMode ? (
          <div className="grid grid-cols-4 gap-2.5">
            <EditSelect label="Product" value={specs.product_type} options={PRODUCT_TYPES_EDIT} onSelect={v => setField('product_type', v)} />
            <EditInput label="Sub Type" value={specs.sub_type} onInput={v => setField('sub_type', v)} />
            <EditSelect label="Metal" value={specs.metal_type} options={METAL_TYPES_EDIT} onSelect={v => setField('metal_type', v)} />
            <EditSelect label="Karat" value={specs.metal_karat} options={METAL_KARATS_EDIT} onSelect={v => setField('metal_karat', v)} />
            <EditSelect label="Color" value={specs.metal_color} options={METAL_COLORS_EDIT} onSelect={v => setField('metal_color', v)} />
            <EditInput label="Metal Wt" value={specs.metal_weight_estimate_grams} onInput={v => setField('metal_weight_estimate_grams', v)} suffix="g" />
            <EditInput label="Total Wt" value={specs.total_piece_weight_estimate_grams} onInput={v => setField('total_piece_weight_estimate_grams', v)} suffix="g" />
            <EditInput label="Category" value={specs.category} onInput={v => setField('category', v)} />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2.5">
            <ViewField label="Product" value={specs.product_type} /><ViewField label="Sub Type" value={specs.sub_type} /><ViewField label="Metal" value={specs.metal_type} /><ViewField label="Karat" value={specs.metal_karat} />
            <ViewField label="Color" value={specs.metal_color} /><ViewField label="Metal Wt" value={specs.metal_weight_estimate_grams} suffix="g" /><ViewField label="Total Wt" value={specs.total_piece_weight_estimate_grams} suffix="g" /><ViewField label="Category" value={specs.category} />
          </div>
        )}
      </div>

      {/* ---- STONES (Full table with add/remove) ---- */}
      <div className="p-2.5 border border-border rounded-lg bg-card">
        <div className="flex items-center justify-between mb-1.5">
          <h4 className="text-[10px] font-bold text-muted-foreground uppercase">Stones ({(specs.stones || []).length})</h4>
          {editMode && (
            <Button variant="outline" size="sm" onClick={addStone} className="h-5 text-[10px] px-2" data-testid="pd-specs-add-stone">
              <Plus size={10} className="mr-0.5" />Add Stone
            </Button>
          )}
        </div>
        {(specs.stones || []).length === 0 && <p className="text-[10px] text-muted-foreground italic">{editMode ? 'No stones — click Add Stone' : 'No stones detected'}</p>}
        {(specs.stones || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-border">
                  {['Type', 'Category', 'Shape', 'Visible', 'Est. Total', 'Carat', 'Setting', 'Size mm', ...(editMode ? [''] : [])].map(h => (
                    <th key={h} className="text-left py-1 px-1 font-medium text-muted-foreground text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(specs.stones || []).map((s, i) => (
                  <tr key={i} className="border-b border-border/30" data-testid={`pd-stone-row-${i}`}>
                    {editMode ? (
                      <>
                        <td className="py-0.5 px-0.5">
                          <Select value={s.type || '__none__'} onValueChange={v => updateStone(i, 'type', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[90px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{STONE_TYPES_EDIT.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-0.5 px-0.5">
                          <Select value={s.category || '__none__'} onValueChange={v => updateStone(i, 'category', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{STONE_CATS_EDIT.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-0.5 px-0.5">
                          <Select value={s.shape || '__none__'} onValueChange={v => updateStone(i, 'shape', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[85px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{STONE_SHAPES_EDIT.map(sh => <SelectItem key={sh} value={sh}>{sh}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-[50px]" type="number" value={s.visible_count ?? s.estimated_count ?? ''} onChange={e => updateStone(i, 'visible_count', e.target.value === '' ? 0 : Number(e.target.value))} /></td>
                        <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-[50px]" type="number" value={s.estimated_total_in_complete_piece ?? s.estimated_count ?? ''} onChange={e => updateStone(i, 'estimated_total_in_complete_piece', e.target.value === '' ? 0 : Number(e.target.value))} /></td>
                        <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-[60px]" type="number" step="0.01" value={s.estimated_carat_total ?? ''} onChange={e => updateStone(i, 'estimated_carat_total', e.target.value === '' ? 0 : Number(e.target.value))} /></td>
                        <td className="py-0.5 px-0.5">
                          <Select value={s.setting_type || '__none__'} onValueChange={v => updateStone(i, 'setting_type', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[90px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{SETTING_TYPES_EDIT.map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-[55px]" value={s.estimated_size_mm ?? ''} onChange={e => updateStone(i, 'estimated_size_mm', e.target.value)} /></td>
                        <td className="py-0.5 px-0.5">
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeStone(i)} data-testid={`pd-remove-stone-${i}`}>
                            <Trash2 size={11} />
                          </Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-1 px-1">
                          <span className="font-semibold">{s.type}</span>
                          {s.specific_description && <p className="text-[9px] text-muted-foreground leading-tight">{s.specific_description}</p>}
                          {s.line_position && <span className="text-[8px] bg-muted px-1 py-0.5 rounded">{s.line_position}</span>}
                        </td>
                        <td className="py-1 px-1">{s.category || '—'}</td>
                        <td className="py-1 px-1">{s.shape}</td>
                        <td className="py-1 px-1">{s.visible_count ?? s.estimated_count}</td>
                        <td className="py-1 px-1 font-semibold">{s.estimated_total_in_complete_piece ?? s.estimated_count}</td>
                        <td className="py-1 px-1">{s.estimated_carat_total}ct</td>
                        <td className="py-1 px-1">{s.setting_type}</td>
                        <td className="py-1 px-1">{s.estimated_size_mm || '—'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---- DIMENSIONS ---- */}
      {(editMode || specs.dimensions) && (
        <div className="p-2.5 border border-border rounded-lg bg-card">
          <h4 className="text-[10px] font-bold text-muted-foreground uppercase mb-1.5">Dimensions</h4>
          {editMode ? (
            <div className="grid grid-cols-5 gap-2.5">
              <EditInput label="Length" value={specs.dimensions?.length_mm} onInput={v => setNested('dimensions', 'length_mm', v)} suffix="mm" />
              <EditInput label="Width" value={specs.dimensions?.width_mm} onInput={v => setNested('dimensions', 'width_mm', v)} suffix="mm" />
              <EditInput label="Height" value={specs.dimensions?.height_mm} onInput={v => setNested('dimensions', 'height_mm', v)} suffix="mm" />
              <EditInput label="Band W" value={specs.dimensions?.band_width_mm} onInput={v => setNested('dimensions', 'band_width_mm', v)} suffix="mm" />
              <EditInput label="Inner Dia" value={specs.dimensions?.inner_diameter_mm} onInput={v => setNested('dimensions', 'inner_diameter_mm', v)} suffix="mm" />
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-2.5">
              <ViewField label="Length" value={specs.dimensions?.length_mm} suffix="mm" /><ViewField label="Width" value={specs.dimensions?.width_mm} suffix="mm" /><ViewField label="Height" value={specs.dimensions?.height_mm} suffix="mm" /><ViewField label="Band W" value={specs.dimensions?.band_width_mm} suffix="mm" /><ViewField label="Inner Dia" value={specs.dimensions?.inner_diameter_mm} suffix="mm" />
            </div>
          )}
        </div>
      )}

      {/* ---- CONSTRUCTION ---- */}
      {(editMode || specs.construction_details) && (
        <div className="p-2.5 border border-border rounded-lg bg-card">
          <h4 className="text-[10px] font-bold text-muted-foreground uppercase mb-1.5">Construction</h4>
          {editMode ? (
            <div className="grid grid-cols-4 gap-2.5">
              <EditInput label="Link Count" value={specs.construction_details?.link_count} onInput={v => setNested('construction_details', 'link_count', v)} />
              <EditSelect label="Link Type" value={specs.construction_details?.link_type} options={LINK_TYPES_EDIT} onSelect={v => setNested('construction_details', 'link_type', v)} />
              <EditSelect label="Clasp" value={specs.construction_details?.clasp_type} options={CLASP_TYPES_EDIT} onSelect={v => setNested('construction_details', 'clasp_type', v)} />
              <EditInput label="Hinge" value={specs.construction_details?.hinge_type} onInput={v => setNested('construction_details', 'hinge_type', v)} />
              <EditInput label="Prongs/Stone" value={specs.construction_details?.prong_count_per_stone} onInput={v => setNested('construction_details', 'prong_count_per_stone', v)} />
              <EditSelect label="Gallery" value={specs.construction_details?.gallery_type} options={GALLERY_TYPES_EDIT} onSelect={v => setNested('construction_details', 'gallery_type', v)} />
              <EditSelect label="Shank" value={specs.construction_details?.shank_type} options={SHANK_TYPES_EDIT} onSelect={v => setNested('construction_details', 'shank_type', v)} />
              <EditSelect label="Back" value={specs.construction_details?.back_type} options={BACK_TYPES_EDIT} onSelect={v => setNested('construction_details', 'back_type', v)} />
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2.5">
              <ViewField label="Links" value={specs.construction_details?.link_count} /><ViewField label="Link Type" value={specs.construction_details?.link_type} /><ViewField label="Clasp" value={specs.construction_details?.clasp_type} /><ViewField label="Hinge" value={specs.construction_details?.hinge_type} />
              <ViewField label="Prongs/Stone" value={specs.construction_details?.prong_count_per_stone} /><ViewField label="Gallery" value={specs.construction_details?.gallery_type} /><ViewField label="Shank" value={specs.construction_details?.shank_type} /><ViewField label="Back" value={specs.construction_details?.back_type} />
            </div>
          )}
        </div>
      )}

      {/* ---- MANUFACTURING ---- */}
      <div className="p-2.5 border border-border rounded-lg bg-card">
        <h4 className="text-[10px] font-bold text-muted-foreground uppercase mb-1.5">Manufacturing</h4>
        {editMode ? (
          <>
            <div className="grid grid-cols-3 gap-2.5">
              <EditSelect label="Complexity" value={specs.manufacturing_complexity} options={COMPLEXITY_OPTS} onSelect={v => setField('manufacturing_complexity', v)} />
              <EditSelect label="Finish" value={specs.finish} options={FINISH_OPTS} onSelect={v => setField('finish', v)} />
              <EditSelect label="Collection" value={specs.collection_potential} options={COLLECTION_OPTS} onSelect={v => setField('collection_potential', v)} />
            </div>
            <div className="mt-2">
              <span className="text-[10px] text-muted-foreground font-medium block mb-1">Techniques</span>
              <div className="flex flex-wrap gap-1 mb-1">
                {(specs.manufacturing_techniques || []).map((t, i) => (
                  <span key={i} className="text-[9px] bg-muted px-1.5 py-0.5 rounded flex items-center gap-1">
                    {t}<button onClick={() => removeTechnique(i)} className="text-muted-foreground hover:text-destructive"><Trash2 size={8} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input placeholder="Add technique..." className="h-6 text-[11px] flex-1"
                  onKeyDown={e => { if (e.key === 'Enter') { addTechnique(e.target.value.trim()); e.target.value = ''; } }}
                />
              </div>
            </div>
            <div className="mt-2">
              <span className="text-[10px] text-muted-foreground font-medium block mb-0.5">Production Notes</span>
              <Textarea value={specs.production_notes || ''} onChange={e => setField('production_notes', e.target.value)} rows={2} className="text-xs" />
            </div>
            <div className="mt-2">
              <span className="text-[10px] text-muted-foreground font-medium block mb-1">Style Tags</span>
              <div className="flex flex-wrap gap-1 mb-1">
                {(specs.style_tags || []).map((t, i) => (
                  <span key={i} className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded flex items-center gap-1">
                    {t}<button onClick={() => onChange({ ...specs, style_tags: specs.style_tags.filter((_, j) => j !== i) })} className="text-primary/50 hover:text-destructive"><Trash2 size={8} /></button>
                  </span>
                ))}
              </div>
              <Input placeholder="Add tag (Enter)..." className="h-6 text-[11px]"
                onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const tags = [...(specs.style_tags || [])]; if (!tags.includes(e.target.value.trim())) { onChange({ ...specs, style_tags: [...tags, e.target.value.trim()] }); } e.target.value = ''; } }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2.5">
              <ViewField label="Complexity" value={specs.manufacturing_complexity} /><ViewField label="Finish" value={specs.finish} /><ViewField label="Collection" value={specs.collection_potential} />
            </div>
            {specs.manufacturing_techniques?.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{specs.manufacturing_techniques.map((t, i) => <span key={i} className="text-[9px] bg-muted px-1.5 py-0.5 rounded">{t}</span>)}</div>}
            {specs.production_notes && <p className="text-[10px] text-muted-foreground mt-1.5 italic">{specs.production_notes}</p>}
            {specs.style_tags?.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{specs.style_tags.map((t, i) => <span key={i} className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">{t}</span>)}</div>}
          </>
        )}
      </div>
    </div>
  );
}
